/**
 * <build-for-everyone> — sections/build-for-everyone.liquid
 * ---------------------------------------------------------------------------
 * The slot model
 */
(function () {
  if (customElements.get("build-for-everyone")) return;

  const DESKTOP_QUERY = "(min-width: 1100px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  const MAX_VISIBLE_DISTANCE = 2;
  const PARK_OVERSHOOT = 1.35;
  const PARK_SCALE = 0.7;
  const COPY_FADE_SPAN = 0.5;
  const COPY_SHIFT_PX = 24;

  const JUMP_DURATION = 1;
  const STATUS_DEBOUNCE_MS = 400;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function easeInOutPower2(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  class BuildForEveryone extends HTMLElement {
    connectedCallback() {
      this.stage = this.closest("[data-bfe-stage]");
      this.items = Array.from(this.querySelectorAll("[data-bfe-item]"));
      this.panels = Array.from(this.querySelectorAll("[data-bfe-panel]"));
      this.navButtons = Array.from(this.querySelectorAll("[data-bfe-nav-button]"));
      this.nav = this.querySelector("[data-bfe-nav]");
      this.status = this.querySelector("[data-bfe-status]");

      if (!this.stage || !this.items.length) return;

      this.activeIndex = 0;
      this.rafId = null;
      this.statusTimer = null;
      this.keyframes = this.buildKeyframes();

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.onScroll = this.onScroll.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onNavKeydown = this.onNavKeydown.bind(this);

      this.desktopQuery.addEventListener("change", this.onModeChange);
      this.reducedMotionQuery.addEventListener("change", this.onModeChange);

      this.navButtons.forEach((button, index) => {
        button.addEventListener("click", () => this.jumpTo(index));
      });

      if (this.nav) this.nav.addEventListener("keydown", this.onNavKeydown);

      this.enterCurrentMode();
    }

    disconnectedCallback() {
      this.teardownPin();
      if (this.desktopQuery) this.desktopQuery.removeEventListener("change", this.onModeChange);
      if (this.reducedMotionQuery) {
        this.reducedMotionQuery.removeEventListener("change", this.onModeChange);
      }
      if (this.nav) this.nav.removeEventListener("keydown", this.onNavKeydown);
      window.clearTimeout(this.statusTimer);
    }

    buildKeyframes() {
      const styles = window.getComputedStyle(this);
      const read = (name, fallback) => {
        const value = parseFloat(styles.getPropertyValue(name));
        return Number.isFinite(value) ? value : fallback;
      };

      const x = read("--bfe-side-x", 68.8);
      const y = read("--bfe-side-y", 39.6);
      const sx = read("--bfe-side-scale-x", 0.29);
      const sy = read("--bfe-side-scale-y", 0.22);

      this.baseRadius = read("--bfe-radius", 12);

      /* Indexed by (d + MAX_VISIBLE_DISTANCE), i.e. keyframes[0] is d = -2. */
      return [
        { x: -x * PARK_OVERSHOOT, y: -y * PARK_OVERSHOOT, sx: sx * PARK_SCALE, sy: sy * PARK_SCALE, opacity: 0 },
        { x: -x, y: -y, sx: sx, sy: sy, opacity: 1 },
        { x: 0, y: 0, sx: 1, sy: 1, opacity: 1 },
        { x: x, y: y, sx: sx, sy: sy, opacity: 1 },
        { x: x * PARK_OVERSHOOT, y: y * PARK_OVERSHOOT, sx: sx * PARK_SCALE, sy: sy * PARK_SCALE, opacity: 0 },
      ];
    }

    /* The pin only runs on desktop AND only when motion is welcome */
    isPinned() {
      return this.desktopQuery.matches && !this.reducedMotionQuery.matches;
    }

    onModeChange() {
      this.teardownPin();
      this.enterCurrentMode();
    }

    enterCurrentMode() {
      if (this.isPinned()) {
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onScroll, { passive: true });
        this.renderFromScroll();
        return;
      }

      this.resetInlineStyles();
      this.setActiveIndex(0, { announce: false });
      /* <swiper-section> initialises  */
      this.refreshSlider();
    }

    teardownPin() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    refreshSlider() {
      const viewport = this.closest(".bfe")?.querySelector(".bfe_slider_viewport");
      if (viewport && viewport.swiperInstance) viewport.swiperInstance.update();
    }

   
    resetInlineStyles() {
      this.items.forEach((item) => {
        item.removeAttribute("style");
        const image = item.firstElementChild;
        if (image) image.removeAttribute("style");
      });
      this.panels.forEach((panel) => panel.removeAttribute("style"));
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.renderFromScroll();
      });
    }

    scrollableDistance() {
      return this.stage.offsetHeight - window.innerHeight;
    }

    progressToPosition(progress) {
      const total = this.items.length;
      if (total <= 1) return 0;
      return clamp(progress * total - 0.5, 0, total - 1);
    }

    renderFromScroll() {
      const scrollable = this.scrollableDistance();
      let progress = 0;
      if (scrollable > 0) {
        progress = clamp(-this.stage.getBoundingClientRect().top / scrollable, 0, 1);
      }
      this.render(this.progressToPosition(progress));
    }

    render(p) {
      this.items.forEach((item, i) => {
        this.renderItem(item, i - p);
      });

      this.panels.forEach((panel, i) => {
        this.renderPanel(panel, i - p);
      });

      this.setActiveIndex(clamp(Math.round(p), 0, this.items.length - 1));
    }

    renderItem(item, d) {
      const distance = clamp(d, -MAX_VISIBLE_DISTANCE, MAX_VISIBLE_DISTANCE);
      const slot = this.interpolateSlot(distance);
      const image = item.firstElementChild;

      /* Layers this far out contribute nothing but compositing cost. */
      if (slot.opacity < 0.01) {
        item.style.visibility = "hidden";
        item.style.opacity = "0";
        return;
      }

      item.style.visibility = "visible";
      item.style.opacity = String(slot.opacity);
      item.style.zIndex = String(Math.round(100 - Math.abs(distance) * 10));
      item.style.transform =
        `translate3d(${slot.x}%, ${slot.y}%, 0) scale(${slot.sx}, ${slot.sy})`;
      item.style.borderRadius =
        `${this.baseRadius / slot.sx}px / ${this.baseRadius / slot.sy}px`;

      if (image) {
        const cover = Math.max(slot.sx, slot.sy);
        image.style.transform = `scale(${cover / slot.sx}, ${cover / slot.sy})`;
      }
    }

   
    interpolateSlot(distance) {
      const position = distance + MAX_VISIBLE_DISTANCE;
      const lower = clamp(Math.floor(position), 0, this.keyframes.length - 1);
      const upper = clamp(lower + 1, 0, this.keyframes.length - 1);
      const t = easeInOutCubic(clamp(position - lower, 0, 1));

      const a = this.keyframes[lower];
      const b = this.keyframes[upper];

      return {
        x: lerp(a.x, b.x, t),
        y: lerp(a.y, b.y, t),
        sx: lerp(a.sx, b.sx, t),
        sy: lerp(a.sy, b.sy, t),
        opacity: lerp(a.opacity, b.opacity, t),
      };
    }

    renderPanel(panel, d) {
      const fade = clamp(1 - Math.abs(d) / COPY_FADE_SPAN, 0, 1);
      const opacity = easeInOutCubic(fade);
      const shift = clamp(d, -1, 1) * COPY_SHIFT_PX;

      panel.style.opacity = String(opacity);
      panel.style.transform = `translate3d(0, ${shift}px, 0)`;
      panel.style.visibility = opacity < 0.01 ? "hidden" : "visible";
    }

    setActiveIndex(index, { announce = true } = {}) {
      if (index === this.activeIndex && this.hasSyncedOnce) return;
      const isFirstSync = !this.hasSyncedOnce;
      this.activeIndex = index;
      this.hasSyncedOnce = true;

      this.navButtons.forEach((button, i) => {
        const isActive = i === index;
        button.classList.toggle("is-active", isActive);
        if (isActive) {
          button.setAttribute("aria-current", "true");
        } else {
          button.removeAttribute("aria-current");
        }
      });

      this.items.forEach((item, i) => item.classList.toggle("is-active", i === index));

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle("is-active", isActive);
        panel.inert = !isActive;
        if (isActive) {
          panel.removeAttribute("aria-hidden");
        } else {
          panel.setAttribute("aria-hidden", "true");
        }
      });

      if (announce && !isFirstSync) this.announce(index);
    }

    
    announce(index) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => {
        const button = this.navButtons[index];
        this.status.textContent = button ? button.textContent.trim() : "";
      }, STATUS_DEBOUNCE_MS);
    }

  
    jumpTo(index) {
      if (!this.isPinned()) return;

      const total = this.items.length;
      const scrollable = this.scrollableDistance();
      if (scrollable <= 0 || total <= 1) return;

      const progress = (index + 0.5) / total;
      const stageTop = this.stage.getBoundingClientRect().top + window.scrollY;
      const target = stageTop + progress * scrollable;

      if (window.lenis) {
        window.lenis.scrollTo(target, { duration: JUMP_DURATION, easing: easeInOutPower2 });
      } else {
        window.scrollTo({ top: target, behavior: "smooth" });
      }
    }

    
    onNavKeydown(event) {
      const current = this.navButtons.indexOf(document.activeElement);
      if (current === -1) return;

      const last = this.navButtons.length - 1;
      let next = null;

      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          next = Math.min(current + 1, last);
          break;
        case "ArrowUp":
        case "ArrowLeft":
          next = Math.max(current - 1, 0);
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      if (next === current) return;
      this.navButtons[next].focus();
      this.jumpTo(next);
    }
  }

  customElements.define("build-for-everyone", BuildForEveryone);
})();
